import { GameDocument } from '../../../types/game-document';
import { MergeAssets } from './index';
import { TaskRoute } from '../../../types/game-document/entities/routes';
import cloneDeep from 'lodash.clonedeep';
import { GetResourceValue } from '../resources';

/**
 * Updates the identified Map in the Game Document.
 * @param gameDocument - The Game Document to modify
 * @param route - The updated Routes
 * @constructor
 */
export const UpdateRoutesAsync = (
  gameDocument: GameDocument,
  route: TaskRoute
) => {
  let routes = gameDocument.assets?.routes ?? [];
  let routeIndex = routes.findIndex(
    (i) => i.id === route.id && i.mapId === route.mapId
  );

  routeIndex >= 0 ? (routes[routeIndex] = route) : routes.push(route);
  return MergeAssets(gameDocument, routes, 'routes');
};

/**
 * Get all routes by map ID.
 * @param gameDocument - The Game Document
 * @param mapId - The ID of the Map to get the Route Entity
 * @constructor
 */
export const GetAssetRoutesByMapId = (
  gameDocument: GameDocument,
  mapAssetId: string
) => {
  return (
    gameDocument?.assets?.routes?.filter((item) => item.mapId === mapAssetId) ||
    []
  );
};

/**
 * Get detail routes by map ID.
 * @param gameDocument - The Game Document
 * @param mapId - The ID of the Map to get the Route Entity
 * @constructor
 */
export const GetDetailRoutesByMapId = async (
  gameDocument: GameDocument,
  mapAssetId: string
) => {
  return (
    gameDocument.assets.routes?.find((item) => item.mapId === mapAssetId) ?? {}
  );
};

/**
 * Get detail routes by route ID and map ID.
 * @param gameDocument - The Game Document
 * @param mapId - The ID of the Map to get the Route Entity
 * @param routeId - The ID of the Route to get the Route Entity
 * @constructor
 */
export const GetDetailRoutesById = async (
  gameDocument: GameDocument,
  mapAssetId: string,
  routeId: string
) => {
  return (
    gameDocument.assets.routes?.find(
      (item) => item.mapId === mapAssetId && item.id === routeId
    ) ?? {}
  );
};

/**
 * Get detail routes by route ID and map ID.
 * @param gameDocument - The Game Document
 * @param mapId - The ID of the Map to get the Route Entity
 * @param routeId - The ID of the Route to get the Route Entity
 * @constructor
 */
export const GetDetailRoutesByRouteId = async (
  gameDocument: GameDocument,
  routeId: string
) => {
  return (
    (await gameDocument.assets.routes?.find((item) => item.id === routeId)) ??
    {}
  );
};

/**
 * Updates the identified Map in the Game Document.
 * @param gameDocument - The Game Document to modify
 * @param route - The updated Routes
 * @constructor
 */
export const RemoveRoutesAsync = async (
  gameDocument: GameDocument,
  route: TaskRoute
) => {
  let gamedoc = cloneDeep(gameDocument);
  gamedoc.assets.routes = gamedoc.assets.routes?.filter(
    (item) => item.id !== route.id
  );

  return gamedoc;
};

export const AUTO_ROUTE_TITLE_SEPARATOR = ' - ';
export const LEGACY_AUTO_ROUTE_TITLE = /^Arrow(-\d+)?$/;

export const IsAutoNamedRoute = (route: TaskRoute): boolean =>
  Boolean(route.automaticallyInfo) && route.isTitleCustomized !== true;

export const BuildAutoRouteTitle = (
  gameDocument: GameDocument,
  fromTaskId: string,
  toTaskId: string
): string => {
  const getTaskTitle = (taskId: string) => {
    const task = gameDocument?.assets?.tasks?.find((t) => t.id === taskId);
    const titleVal = task?.titleResId
      ? GetResourceValue(gameDocument, task.titleResId)
      : '';
    return titleVal.trim() || 'Untitled task';
  };
  return `${getTaskTitle(fromTaskId)}${AUTO_ROUTE_TITLE_SEPARATOR}${getTaskTitle(toTaskId)}`;
};

/**
 * Synchronizes auto-named routes title with current task titles.
 * Pure and idempotent: returns the original gameDocument reference if no changes were made.
 */
export const SyncAutoRouteTitles = (gameDocument: GameDocument): GameDocument => {
  if (!gameDocument?.assets?.routes || gameDocument.assets.routes.length === 0) {
    return gameDocument;
  }

  let docChanged = false;
  let routesChanged = false;
  let resourcesChanged = false;

  const routes = [...gameDocument.assets.routes];
  const resources = gameDocument.resources ? [...gameDocument.resources] : [];

  for (let i = 0; i < routes.length; i++) {
    const route = routes[i];
    if (!route.automaticallyInfo) {
      continue;
    }

    let isCustomized = route.isTitleCustomized;

    // Resolve legacy flag if undefined (migrating legacy Arrow / Arrow-N routes)
    if (isCustomized === undefined) {
      const currentTitle = GetResourceValue(gameDocument, route.titleResId ?? '');
      if (LEGACY_AUTO_ROUTE_TITLE.test(currentTitle.trim())) {
        isCustomized = false;
      } else {
        isCustomized = true;
      }
      routes[i] = { ...route, isTitleCustomized: isCustomized };
      routesChanged = true;
      docChanged = true;
    }

    if (!isCustomized) {
      const targetTitle = BuildAutoRouteTitle(
        gameDocument,
        routes[i].fromTaskId,
        routes[i].toTaskId
      );

      const resIndex = resources.findIndex((r) => r.id === routes[i].titleResId);
      if (resIndex !== -1) {
        if (resources[resIndex].value !== targetTitle) {
          resources[resIndex] = {
            ...resources[resIndex],
            value: targetTitle
          };
          resourcesChanged = true;
          docChanged = true;
        }
      }

      if (routes[i].name !== targetTitle) {
        routes[i] = {
          ...routes[i],
          name: targetTitle
        };
        routesChanged = true;
        docChanged = true;
      }
    }
  }

  if (!docChanged) {
    return gameDocument;
  }

  return {
    ...gameDocument,
    assets: {
      ...gameDocument.assets,
      ...(routesChanged ? { routes } : {})
    },
    ...(resourcesChanged ? { resources } : {})
  };
};
